import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useAuthStore } from '../../store/auth-store';
import {
  CrudDrawer,
  TextField,
  NumberField,
  SelectField,
  CheckField,
  Field,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';
import type { Category } from './categories-tab';
import { mediaSrc } from '../../api/media';
import { BannerCropper, type CropSpec } from './banner-cropper';

type BannerKind = 'GENERAL' | 'OFFER' | 'AD' | 'ANNOUNCEMENT';
type Placement = 'HOME_HERO' | 'HOME_STRIP' | 'CATEGORY_TOP';
type TargetType = 'CATEGORY' | 'PRODUCT' | 'URL' | 'NONE';

interface Banner {
  id: string;
  title: string;
  kind?: BannerKind;
  subtitle?: string | null;
  ctaLabel?: string | null;
  imageUrl: string;
  targetType: TargetType;
  targetId: string | null;
  targetUrl: string | null;
  placement: Placement;
  sortOrder: number;
  startAt: string | null;
  endAt: string | null;
  isActive: boolean;
}

interface ProductHit {
  id: string;
  title: string;
  images?: Array<{ url: string }>;
}

// Placement and target type stay hardcoded, unlike the lists on the Master Data
// page: each value maps to a specific slot the customer app renders and a code
// path that handles the tap. A new one needs app work regardless, so making it
// admin-editable would only allow creating banners nothing displays.
//
// `size` / `crop` describe the slot the app draws each placement into, edge to
// edge (cover), so an image of that shape shows whole and anything else loses
// its edges. Uploads go through the cropper, which exports exactly this shape.
//
// The hero is the customer home's full-bleed top section: roughly the phone's
// width × 1.18 plus the status bar (customer_home_page.dart), i.e. ~4:5
// portrait. It was advertised as 2:1, so landscape art lost ~60% of its width.
// Its top ~30% sits under the glass header + category pills and the bottom
// ~10% under the search bar.
const PLACEMENTS: Array<{ value: Placement; label: string; size: string; ratio: number; crop: CropSpec }> = [
  {
    value: 'HOME_HERO',
    label: 'Home — hero carousel',
    size: '1080 × 1350 px (4:5, portrait)',
    ratio: 0.8,
    crop: { ratio: 0.8, outputWidth: 1080, coveredTop: 0.3, coveredBottom: 0.1 },
  },
  {
    value: 'HOME_STRIP',
    label: 'Home — mid-page strip',
    size: '1500 × 500 px (3:1)',
    ratio: 3,
    crop: { ratio: 3, outputWidth: 1500 },
  },
  {
    value: 'CATEGORY_TOP',
    label: 'Category listing — top',
    size: '1500 × 500 px (3:1)',
    ratio: 3,
    crop: { ratio: 3, outputWidth: 1500 },
  },
];

const KINDS: Array<{ value: BannerKind; label: string; hint: string; badge: string }> = [
  { value: 'OFFER', label: 'Offer', hint: 'A sale or discount. Shows a small "Offer" tag.', badge: 'badge-accent' },
  { value: 'AD', label: 'Ad', hint: 'Paid or partner promotion. Always labelled "Ad" in the app.', badge: 'badge-jamun' },
  { value: 'ANNOUNCEMENT', label: 'Announcement', hint: 'News, a new collection, a service update.', badge: 'badge-info' },
  { value: 'GENERAL', label: 'General', hint: 'Anything else. No tag.', badge: '' },
];

const TARGET_TYPES: Array<{ value: TargetType; label: string }> = [
  { value: 'NONE', label: 'Not tappable' },
  { value: 'CATEGORY', label: 'Open a category' },
  { value: 'PRODUCT', label: 'Open a product' },
  { value: 'URL', label: 'Open a link' },
];

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// The file the admin picks. It is cropped and re-encoded in the browser before
// upload, so the API's 5 MB cap applies to the much smaller result.
const SOURCE_MAX_BYTES = 25 * 1024 * 1024;

const EMPTY = {
  title: '',
  kind: 'GENERAL' as BannerKind,
  subtitle: '',
  ctaLabel: '',
  imageUrl: '',
  placement: 'HOME_HERO' as Placement,
  targetType: 'NONE' as TargetType,
  targetId: '',
  targetLabel: '',
  targetUrl: '',
  sortOrder: '',
  startAt: '',
  endAt: '',
  isActive: true,
};
type Form = typeof EMPTY;

/** `<input type="datetime-local">` wants 'YYYY-MM-DDTHH:mm' in local time; the
 *  API speaks ISO-8601 UTC. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
  return v ? new Date(v).toISOString() : null;
}

type Status = 'Live' | 'Scheduled' | 'Ended' | 'Off';

/** What a shopper sees right now. Active is only the master switch — a banner
 *  outside its window is not shown, and "why isn't my banner live" should be
 *  answerable from this table. */
function statusOf(b: Banner, now = Date.now()): Status {
  if (!b.isActive) return 'Off';
  if (b.startAt && new Date(b.startAt).getTime() > now) return 'Scheduled';
  if (b.endAt && new Date(b.endAt).getTime() < now) return 'Ended';
  return 'Live';
}

const STATUS_BADGE: Record<Status, string> = {
  Live: 'badge badge-success badge-dot',
  Scheduled: 'badge badge-info',
  Ended: 'badge',
  Off: 'badge badge-outline',
};

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

function kindOf(b: { kind?: BannerKind }): (typeof KINDS)[number] {
  return KINDS.find((k) => k.value === (b.kind ?? 'GENERAL')) ?? KINDS[3]!;
}

export function BannersTab() {
  const canWrite = useAuthStore((s) => s.hasScope)('catalog.banner.write');
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<Banner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Banner | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY);
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  /** Image open in the cropper; `revoke` when it is an object URL we created. */
  const [cropping, setCropping] = useState<{ src: string; revoke: boolean } | null>(null);

  const categoryName = (id: string | null) => categories.find((c) => c.id === id)?.name;

  const targetText = (r: Banner): string =>
    r.targetType === 'NONE'
      ? 'Not tappable'
      : r.targetType === 'URL'
        ? r.targetUrl ?? ''
        : r.targetType === 'CATEGORY'
          ? `Category: ${categoryName(r.targetId) ?? `#${r.targetId}`}`
          : `Product #${r.targetId}`;

  const placementLabel = (p: Placement) => PLACEMENTS.find((x) => x.value === p)?.label ?? p;

  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.title, r.subtitle, kindOf(r).label, placementLabel(r.placement), targetText(r), statusOf(r),
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const [banners, cats] = await Promise.all([
        api.get<Banner[]>('/admin/banners'),
        // Powers the category picker when targetType is CATEGORY. A failure
        // here shouldn't blank the banner list, so it's tolerated separately.
        api.get<Category[]>('/admin/categories').catch(() => ({ data: [] as Category[] })),
      ]);
      setRows(banners.data);
      setCategories(cats.data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => { setEditing(null); setCreating(true); setForm(EMPTY); setImageSize(null); };

  const openEdit = (row: Banner) => {
    setCreating(false); setEditing(row); setImageSize(null);
    setForm({
      title: row.title,
      kind: row.kind ?? 'GENERAL',
      subtitle: row.subtitle ?? '',
      ctaLabel: row.ctaLabel ?? '',
      imageUrl: row.imageUrl,
      placement: row.placement,
      targetType: row.targetType,
      targetId: row.targetId ?? '',
      targetLabel: '',
      targetUrl: row.targetUrl ?? '',
      sortOrder: String(row.sortOrder),
      startAt: toLocalInput(row.startAt),
      endAt: toLocalInput(row.endAt),
      isActive: row.isActive,
    });
  };

  const close = () => { setEditing(null); setCreating(false); };

  /** A picked file opens in the cropper; the crop, not the file, is uploaded. */
  const pick = (file: File) => {
    if (fileRef.current) fileRef.current.value = '';
    if (!IMAGE_TYPES.includes(file.type)) { toast.error('Upload a JPG, PNG or WebP image'); return; }
    if (file.size > SOURCE_MAX_BYTES) { toast.error('Pick an image under 25 MB'); return; }
    setCropping({ src: URL.createObjectURL(file), revoke: true });
  };

  const closeCropper = () => {
    if (cropping?.revoke) URL.revokeObjectURL(cropping.src);
    setCropping(null);
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const { data } = await api.post<{ url: string }>('/admin/banners/image', file, {
        headers: { 'Content-Type': file.type },
        timeout: 60_000,
      });
      setForm((f) => ({ ...f, imageUrl: data.url }));
      toast.success('Image uploaded');
      closeCropper();
    } catch (err) {
      // Keep the cropper open so the admin can retry without re-cropping.
      toast.error(extractErr(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.imageUrl.trim()) { toast.error('Upload an image or paste its URL'); return; }
    if (form.targetType === 'URL' && !/^https?:\/\//i.test(form.targetUrl.trim())) {
      toast.error('The link must start with https://'); return;
    }
    if ((form.targetType === 'CATEGORY' || form.targetType === 'PRODUCT') && !/^\d+$/.test(form.targetId.trim())) {
      toast.error(`Pick the ${form.targetType.toLowerCase()} this banner opens`); return;
    }
    if (form.startAt && form.endAt && new Date(form.endAt) <= new Date(form.startAt)) {
      toast.error('The end time must be after the start time'); return;
    }

    const body = {
      title: form.title.trim(),
      kind: form.kind,
      subtitle: form.subtitle.trim() || null,
      ctaLabel: form.ctaLabel.trim() || null,
      imageUrl: form.imageUrl.trim(),
      placement: form.placement,
      targetType: form.targetType,
      targetId: form.targetType === 'CATEGORY' || form.targetType === 'PRODUCT' ? form.targetId.trim() : null,
      targetUrl: form.targetType === 'URL' ? form.targetUrl.trim() : null,
      ...(form.sortOrder !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
      startAt: fromLocalInput(form.startAt),
      endAt: fromLocalInput(form.endAt),
      isActive: form.isActive,
    };
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/banners', body);
        toast.success('Banner created');
      } else if (editing) {
        await api.put(`/admin/banners/${editing.id}`, body);
        toast.success('Banner updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const ok = await confirm({
      title: `Delete "${editing.title}"?`,
      description: 'It disappears from the customer app straight away and can\'t be restored. Switch it off instead to keep it for later.',
      confirmLabel: 'Delete banner',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/banners/${editing.id}`);
      toast.success('Banner deleted');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: Banner, next: boolean) => {
    // Switching off pulls it from the app at once, so ask first; switching
    // back on is harmless and stays one click.
    if (!next) {
      const ok = await confirm({
        title: `Switch off "${row.title}"?`,
        description: 'Shoppers stop seeing it on their next home refresh. You can switch it back on any time.',
        confirmLabel: 'Switch off',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/banners/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  const placement = PLACEMENTS.find((p) => p.value === form.placement) ?? PLACEMENTS[0]!;
  const ratioOff =
    imageSize && Math.abs(imageSize.w / imageSize.h - placement.ratio) / placement.ratio > 0.15;

  return (
    <>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-surface-line px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Banners</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              Offers, ads and announcements on the customer app&apos;s home. Schedule them, or
              leave the dates empty to run until switched off.
            </p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              className="btn btn-primary btn-sm shrink-0"
            >
              <Symbol name="add" size={16} /> Add banner
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-b border-surface-line px-5 py-3">
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search by title, kind, placement, target or status"
              resultCount={filtered.length}
            />
          </div>
        )}

        {loading ? (
          <SkeletonRows cols={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="view_carousel"
            title="No banners yet"
            body="Add an offer, an ad or an announcement and it shows at the top of the customer app's home."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for "${query.trim()}"`}
            body="Try a title, Offer / Ad / Announcement, a placement, or Live / Scheduled / Ended / Off."
            action={
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>
                Clear search
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Banner</th>
                <th>Status</th>
                <th className="hidden md:table-cell">Placement</th>
                <th className="hidden lg:table-cell">Target</th>
                <th className="!text-right">Order</th>
                <th className="!text-right">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const status = statusOf(r);
                const kind = kindOf(r);
                return (
                  <tr key={r.id} className={r.isActive ? '' : 'bg-surface-sunk/60'}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <img
                          src={mediaSrc(r.imageUrl)}
                          alt=""
                          className="h-9 w-[72px] shrink-0 rounded-xs bg-surface-2 object-cover"
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium text-ink-900">{r.title}</span>
                            {kind.value !== 'GENERAL' && (
                              <span className={`badge ${kind.badge}`}>{kind.label}</span>
                            )}
                          </div>
                          {r.subtitle && (
                            <div className="truncate text-[12.5px] text-ink-500">{r.subtitle}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={STATUS_BADGE[status]}>{status}</span>
                      {status === 'Scheduled' && (
                        <div className="mt-0.5 text-[12px] text-ink-500">from {fmt(r.startAt)}</div>
                      )}
                      {status === 'Ended' && (
                        <div className="mt-0.5 text-[12px] text-ink-500">{fmt(r.endAt)}</div>
                      )}
                    </td>
                    <td className="hidden text-ink-700 md:table-cell">{placementLabel(r.placement)}</td>
                    <td className="hidden max-w-[260px] truncate text-ink-500 lg:table-cell">{targetText(r)}</td>
                    <td className="text-right sku text-ink-500">{r.sortOrder}</td>
                    <td>
                      <div className="flex justify-end">
                        <Toggle
                          checked={r.isActive}
                          disabled={!canWrite}
                          aria-label={`${r.isActive ? 'Switch off' : 'Switch on'} ${r.title}`}
                          onChange={(v) => void toggleActive(r, v)}
                        />
                      </div>
                    </td>
                    <td className="text-right">
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="btn btn-ghost btn-sm"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? 'New banner' : `Edit ${editing?.title ?? ''}`}
        busy={busy || uploading}
        onClose={close}
        onSubmit={() => void save()}
        secondaryAction={editing ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined}
      >
        <Field label="Preview" hint="Roughly how it appears in the customer app.">
          <BannerPreview form={form} ratio={placement.ratio} onImageSize={setImageSize} />
        </Field>

        <SelectField
          label="Kind"
          value={form.kind}
          onChange={(v) => setForm((f) => ({ ...f, kind: v as BannerKind }))}
          options={KINDS.map((k) => ({ value: k.value, label: k.label }))}
          hint={KINDS.find((k) => k.value === form.kind)?.hint}
        />
        <TextField
          label="Title"
          value={form.title}
          onChange={(v) => setForm((f) => ({ ...f, title: v }))}
          placeholder="Festive edit — up to 40% off"
          maxLength={160}
          required
          hint="Read aloud to screen-reader users. Drawn over the image only when there's a subtitle or button."
        />
        <TextField
          label="Subtitle"
          value={form.subtitle}
          onChange={(v) => setForm((f) => ({ ...f, subtitle: v }))}
          placeholder="Handloom sarees from ₹999"
          maxLength={200}
          hint="Optional. Leave subtitle and button empty if the text is already in the image."
        />
        <TextField
          label="Button label"
          value={form.ctaLabel}
          onChange={(v) => setForm((f) => ({ ...f, ctaLabel: v }))}
          placeholder="Shop now"
          maxLength={40}
          hint="Optional. Keep it to two or three words."
        />

        <SelectField
          label="Placement"
          value={form.placement}
          onChange={(v) => setForm((f) => ({ ...f, placement: v as Placement }))}
          options={PLACEMENTS.map((p) => ({ value: p.value, label: p.label }))}
          required
        />

        <Field
          label="Image"
          required
          hint={`Shown at ${placement.size}. You crop to this shape after picking a JPG / PNG / WebP.`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={IMAGE_TYPES.join(',')}
              className="sr-only"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); }}
            />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={uploading || busy}
              onClick={() => fileRef.current?.click()}
            >
              <Symbol name={uploading ? 'progress_activity' : 'upload'} size={16} />
              {uploading ? 'Uploading…' : form.imageUrl ? 'Replace image' : 'Upload image'}
            </button>
            {/* Re-crop the current image to this placement's shape — fixes a
                banner uploaded before the cropper, or after a placement change. */}
            {form.imageUrl && mediaSrc(form.imageUrl).startsWith('/media/') && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={uploading || busy}
                onClick={() => setCropping({ src: mediaSrc(form.imageUrl), revoke: false })}
              >
                <Symbol name="crop" size={16} /> Adjust crop
              </button>
            )}
            {imageSize && (
              <span className={`text-[12.5px] ${ratioOff ? 'text-warning-ink' : 'text-ink-500'}`}>
                {imageSize.w} × {imageSize.h}px{ratioOff ? ' — different shape, edges will be cropped' : ''}
              </span>
            )}
          </div>
        </Field>
        <TextField
          label="…or image URL"
          value={form.imageUrl}
          onChange={(v) => setForm((f) => ({ ...f, imageUrl: v }))}
          placeholder="https://…"
        />

        <SelectField
          label="On tap"
          value={form.targetType}
          onChange={(v) => setForm((f) => ({ ...f, targetType: v as TargetType, targetId: '', targetLabel: '' }))}
          options={TARGET_TYPES}
        />
        {form.targetType === 'CATEGORY' && (
          <SelectField
            label="Category"
            value={form.targetId}
            onChange={(v) => setForm((f) => ({ ...f, targetId: v }))}
            options={categories.map((c) => ({
              value: c.id,
              label: c.parentId ? `— ${c.name}` : c.name,
            }))}
            placeholder="Choose a category"
            required
          />
        )}
        {form.targetType === 'PRODUCT' && (
          <ProductPicker
            value={form.targetId}
            label={form.targetLabel}
            onChange={(id, label) => setForm((f) => ({ ...f, targetId: id, targetLabel: label }))}
          />
        )}
        {form.targetType === 'URL' && (
          <TextField
            label="Link"
            value={form.targetUrl}
            onChange={(v) => setForm((f) => ({ ...f, targetUrl: v }))}
            placeholder="https://…"
            required
            hint="Opens in the phone's browser. Use for partner ads and offer pages."
          />
        )}
        <NumberField
          label="Sort order"
          value={form.sortOrder}
          onChange={(v) => setForm((f) => ({ ...f, sortOrder: v }))}
          placeholder="0"
          min={0}
          hint="Position within its placement. Lower appears first."
        />
        <Field label="Runs from" hint="Leave both empty to run until switched off.">
          <input
            type="datetime-local"
            value={form.startAt}
            onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
            className="input"
          />
        </Field>
        <Field label="Runs until">
          <input
            type="datetime-local"
            value={form.endAt}
            onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
            className="input"
          />
        </Field>
        <CheckField
          label="Active"
          hint="The master switch. A banner still only shows inside its scheduled window."
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>

      {cropping && (
        <BannerCropper
          src={cropping.src}
          spec={placement.crop}
          busy={uploading}
          onCancel={closeCropper}
          onDone={(file) => void upload(file)}
        />
      )}
    </>
  );
}

/**
 * A scaled copy of the app's banner: radius 16, image cover, and — only when
 * there's a subtitle or button — a bottom scrim with the Gloock title, the
 * subtitle and a white CTA chip. Ads carry the "Ad" pill top-left.
 */
function BannerPreview({
  form,
  ratio,
  onImageSize,
}: {
  form: Form;
  ratio: number;
  onImageSize: (s: { w: number; h: number } | null) => void;
}) {
  const src = mediaSrc(form.imageUrl);
  const caption = form.subtitle.trim() !== '' || form.ctaLabel.trim() !== '';
  const pill = form.kind === 'AD' ? 'Ad' : form.kind === 'OFFER' ? 'Offer' : null;

  return (
    <div
      className="relative mx-auto w-full overflow-hidden rounded-[16px] bg-jamun-800"
      // A portrait hero at full drawer width would be taller than the screen.
      style={{ aspectRatio: String(ratio), maxWidth: ratio < 1 ? 260 : undefined }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onLoad={(e) => onImageSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          onError={() => onImageSize(null)}
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-[13px] text-white/70">
          <span className="flex items-center gap-1.5"><Symbol name="image" size={18} /> Upload an image</span>
        </div>
      )}
      {pill && (
        <span className="absolute left-2.5 top-2.5 rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-semibold text-ink-900">
          {pill}
        </span>
      )}
      {caption && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/35 to-transparent px-3.5 pb-3 pt-8">
          <div className="font-display text-[18px] leading-tight text-white">{form.title || 'Banner title'}</div>
          {form.subtitle.trim() && (
            <div className="mt-0.5 line-clamp-2 text-[12.5px] text-white/90">{form.subtitle}</div>
          )}
          {form.ctaLabel.trim() && (
            <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[12px] font-semibold text-ink-900">
              {form.ctaLabel} <Symbol name="arrow_forward" size={14} />
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Product search for PRODUCT banners. Live products only — a banner pointing at
 * a draft or delisted product would open nothing (the API drops such targets).
 * Falls back to typing the id when the admin can't read the products list.
 */
function ProductPicker({
  value,
  label,
  onChange,
}: {
  value: string;
  label: string;
  onChange: (id: string, label: string) => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ProductHit[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return; }
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get<{ items: ProductHit[] }>('/admin/products', {
          params: { q: term, status: 'ACTIVE', limit: 8 },
        });
        setHits(data.items ?? []);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [q]);

  return (
    <Field
      label="Product"
      required
      hint={value ? `Opens product #${value}${label ? ` — ${label}` : ''}.` : 'Search live products by name, or type the product id.'}
    >
      <input
        type="search"
        className="input"
        value={q}
        placeholder={value ? `#${value}${label ? ` ${label}` : ''}` : 'Search products or enter an id'}
        onChange={(e) => {
          const v = e.target.value;
          setQ(v);
          if (/^\d+$/.test(v.trim())) onChange(v.trim(), '');
        }}
      />
      {(hits.length > 0 || searching) && (
        <ul className="mt-1.5 max-h-56 overflow-y-auto rounded-md border border-surface-line bg-white">
          {searching && hits.length === 0 && (
            <li className="px-3 py-2 text-[13px] text-ink-500">Searching…</li>
          )}
          {hits.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13.5px] hover:bg-surface-2 ${p.id === value ? 'bg-admin-soft' : ''}`}
                onClick={() => { onChange(p.id, p.title); setQ(''); setHits([]); }}
              >
                {p.images?.[0]?.url ? (
                  <img src={mediaSrc(p.images[0].url)} alt="" className="h-8 w-8 shrink-0 rounded-xs object-cover" />
                ) : (
                  <span className="h-8 w-8 shrink-0 rounded-xs bg-surface-2" />
                )}
                <span className="min-w-0 flex-1 truncate text-ink-900">{p.title}</span>
                <span className="sku text-ink-500">#{p.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
}
